/**
 * Watermark / text-toolbar font catalogs.
 * Text ≈ 100+ · Signature ≈ 50 (Google + system fallbacks).
 */

const sans = (name) => ({
  id: name,
  label: name,
  stack: `"${name}", Arial, Helvetica, sans-serif`,
});

const serif = (name) => ({
  id: name,
  label: name,
  stack: `"${name}", Georgia, "Times New Roman", serif`,
});

const mono = (name) => ({
  id: name,
  label: name,
  stack: `"${name}", "Courier New", Courier, monospace`,
});

const display = (name) => ({
  id: name,
  label: name,
  stack: `"${name}", Impact, Arial Black, sans-serif`,
});

const script = (name) => ({
  id: name,
  label: name,
  stack: `"${name}", "Segoe Script", "Bradley Hand", cursive`,
});

const system = (id, label, stack) => ({ id, label, stack });

const dedupeById = (list) => {
  const seen = new Set();
  return list.filter((f) => {
    if (seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });
};

const SYSTEM_TEXT = [
  system("Arial", "Arial", "Arial, Helvetica, sans-serif"),
  system("Helvetica", "Helvetica", "Helvetica, Arial, sans-serif"),
  system("Georgia", "Georgia", "Georgia, Times New Roman, serif"),
  system(
    "Times New Roman",
    "Times New Roman",
    '"Times New Roman", Times, serif'
  ),
  system("Verdana", "Verdana", "Verdana, Geneva, sans-serif"),
  system("Tahoma", "Tahoma", "Tahoma, Geneva, sans-serif"),
  system(
    "Trebuchet MS",
    "Trebuchet MS",
    '"Trebuchet MS", Helvetica, sans-serif'
  ),
  system(
    "Courier New",
    "Courier New",
    '"Courier New", Courier, monospace'
  ),
  system("Impact", "Impact", "Impact, Haettenschweiler, sans-serif"),
  system(
    "Comic Sans MS",
    "Comic Sans MS",
    '"Comic Sans MS", "Comic Sans", cursive'
  ),
  system(
    "Palatino Linotype",
    "Palatino",
    '"Palatino Linotype", Palatino, serif'
  ),
  system(
    "Lucida Console",
    "Lucida Console",
    '"Lucida Console", Monaco, monospace'
  ),
  system(
    "Lucida Sans Unicode",
    "Lucida Sans",
    '"Lucida Sans Unicode", "Lucida Grande", sans-serif'
  ),
  system(
    "Gill Sans",
    "Gill Sans",
    '"Gill Sans", "Gill Sans MT", Calibri, sans-serif'
  ),
  system("Candara", "Candara", "Candara, Calibri, sans-serif"),
  system("Calibri", "Calibri", "Calibri, Candara, Arial, sans-serif"),
  system("Cambria", "Cambria", "Cambria, Georgia, serif"),
  system("Constantia", "Constantia", "Constantia, Georgia, serif"),
  system("Corbel", "Corbel", "Corbel, Arial, sans-serif"),
  system("Segoe UI", "Segoe UI", '"Segoe UI", Tahoma, sans-serif'),
];

const GOOGLE_SANS = [
  "Roboto",
  "Open Sans",
  "Montserrat",
  "Lato",
  "Poppins",
  "Inter",
  "Nunito",
  "Nunito Sans",
  "Raleway",
  "Ubuntu",
  "Rubik",
  "Work Sans",
  "Noto Sans",
  "Source Sans 3",
  "Mulish",
  "Barlow",
  "Barlow Condensed",
  "DM Sans",
  "Manrope",
  "Outfit",
  "Figtree",
  "Plus Jakarta Sans",
  "Space Grotesk",
  "Sora",
  "Lexend",
  "Albert Sans",
  "Red Hat Display",
  "IBM Plex Sans",
  "PT Sans",
  "Cabin",
  "Karla",
  "Exo 2",
  "Josefin Sans",
  "Quicksand",
  "Comfortaa",
  "Varela Round",
  "Abel",
  "Asap",
  "Maven Pro",
  "Titillium Web",
  "Archivo",
  "Archivo Narrow",
  "Prompt",
  "Kanit",
  "Be Vietnam Pro",
  "Public Sans",
  "Overpass",
  "Hind",
  "Mukta",
  "Fira Sans",
  "Oxygen",
  "Catamaran",
  "Encode Sans",
  "Chivo",
].map(sans);

const GOOGLE_DISPLAY = [
  "Oswald",
  "Anton",
  "Bebas Neue",
  "Teko",
  "Russo One",
  "Fjalla One",
  "Alfa Slab One",
  "Righteous",
  "Bangers",
  "Fredoka",
  "Lilita One",
].map(display);

const GOOGLE_SERIF = [
  "Playfair Display",
  "Merriweather",
  "Lora",
  "PT Serif",
  "Noto Serif",
  "Libre Baskerville",
  "Cormorant Garamond",
  "EB Garamond",
  "Crimson Text",
  "Source Serif 4",
  "Bitter",
  "Domine",
  "Vollkorn",
  "Cardo",
  "Spectral",
  "Literata",
  "Fraunces",
  "DM Serif Display",
  "DM Serif Text",
  "Libre Caslon Text",
  "Old Standard TT",
  "Zilla Slab",
  "Roboto Slab",
  "Arvo",
  "Crete Round",
].map(serif);

const GOOGLE_MONO = [
  "Space Mono",
  "Roboto Mono",
  "Source Code Pro",
  "IBM Plex Mono",
  "Fira Code",
  "JetBrains Mono",
  "Inconsolata",
  "Courier Prime",
  "Ubuntu Mono",
  "PT Mono",
].map(mono);

/** Google family names used for CSS @import (text + signature). */
export const WATERMARK_GOOGLE_TEXT_FAMILIES = [
  ...GOOGLE_SANS,
  ...GOOGLE_DISPLAY,
  ...GOOGLE_SERIF,
  ...GOOGLE_MONO,
].map((f) => f.id);

export const WATERMARK_GOOGLE_SIGNATURE_FAMILIES = [
  "Dancing Script",
  "Great Vibes",
  "Pacifico",
  "Satisfy",
  "Allura",
  "Caveat",
  "Sacramento",
  "Homemade Apple",
  "Yellowtail",
  "Kaushan Script",
  "Alex Brush",
  "Cookie",
  "Courgette",
  "Gloria Hallelujah",
  "Indie Flower",
  "Patrick Hand",
  "Permanent Marker",
  "Shadows Into Light",
  "Amatic SC",
  "Covered By Your Grace",
  "Handlee",
  "Kalam",
  "Architects Daughter",
  "Bad Script",
  "Calligraffitti",
  "Cedarville Cursive",
  "Clicker Script",
  "Coming Soon",
  "Crafty Girls",
  "Damion",
  "Devonshire",
  "Engagement",
  "Euphoria Script",
  "Fondamento",
  "Give You Glory",
  "Herr Von Muellerhoff",
  "Italianno",
  "Jim Nightshade",
  "La Belle Aurore",
  "Marck Script",
  "Miss Fajardose",
  "Monsieur La Doulaise",
  "Mr De Haviland",
  "Mrs Saint Delafield",
  "Norican",
  "Nothing You Could Do",
  "Pinyon Script",
  "Rouge Script",
  "Tangerine",
];

/** ~100+ standard text fonts. */
export const WATERMARK_TEXT_FONTS = dedupeById([
  ...SYSTEM_TEXT,
  ...GOOGLE_SANS,
  ...GOOGLE_DISPLAY,
  ...GOOGLE_SERIF,
  ...GOOGLE_MONO,
]);

/** ~50 script / signature fonts. */
export const WATERMARK_SIGNATURE_FONTS = dedupeById([
  ...WATERMARK_GOOGLE_SIGNATURE_FAMILIES.map(script),
  system(
    "Segoe Script",
    "Segoe Script",
    '"Segoe Script", "Bradley Hand", cursive'
  ),
  system(
    "Brush Script MT",
    "Brush Script",
    '"Brush Script MT", "Segoe Script", cursive'
  ),
  system(
    "Bradley Hand",
    "Bradley Hand",
    '"Bradley Hand", "Segoe Script", cursive'
  ),
]);
